import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useThemeStore } from '@/store/ConfigStore';
import { useToast } from '@/components/Toast';
import { validateNumericID } from '@/utils/validator';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Pagination } from "@/components/Pagination";
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { MobileTableCard } from '@/components/MobileCards';
import type { Commission, CumulativeCommission } from '@/types/financialDetails';
import { fetchCommissionList, exportData, fetchContractCode } from '@/services/financialDetails';
import dayjs from 'dayjs';
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';

export const routeConfig: RouteConfig = {
    title: 'route.financialDetails.commission',
    layout: true,
    sort: 0
};

const Commission = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore(); // 引入主题状态
    const toast = useToast();

    // const [activeTab, setActiveTab] = useState<'spot' | 'futures' | 'alpha'>('spot');
    const [customerUID, setCustomerUID] = useState('');
    const [orderID, setOrderID] = useState('');
    const [rebateType, setRebateType] = useState(null);
    const [transactionTime, setTransactionTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [tradingPair, setTradingPair] = useState(null);
    const [transactionMethod, setCommissionMethod] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [commissionData, setCommissionData] = useState<Commission[]>([]);
    const [cumulativeData, setCumulativeData] = useState<CumulativeCommission | null>(null);
    // Track copied row by orderID for unique icon update
    const [copiedOrderID, setCopiedOrderID] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [contractCodes, setContractCodes] = useState<string[]>([]);

    const pageType = 1; // 1 - Commission, 2 - transaction, 3 - summary

    const buildCommissionParams = (customerUID: string, orderID: string, rebateType: number | null, transactionTime: [Dayjs | null, Dayjs | null], tradingPair: number | null, transactionMethod: number | null) => {
        let params: any = {};
        if (customerUID) {
            params.user_id = customerUID;
        }
        if (orderID) {
            params.order_id = orderID;
        }
        if (rebateType !== 0 && rebateType !== null) {
            params.rebate_type = rebateType;
        }
        if (transactionTime[0] && transactionTime[1]) {
            params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        }
        if (tradingPair !== 0 && tradingPair !== null) {
            params.contract_code = tradingPair;
        }
        if (transactionMethod !== 0 && transactionMethod !== null) {
            params.trading_method = transactionMethod;
        }
        params.agent_id = UserStore.userInfo?.id;
        params.invite_code = UserStore.userInfo?.inviteCode;
        return params;
    };

    const handleReset = async () => {
        try {
            setCustomerUID('');
            setOrderID('');
            setRebateType(null);
            setTransactionTime([null, null]);
            setTradingPair(null);
            setCommissionMethod(null);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters - use empty values since we just reset them
            const params = buildCommissionParams('', '', null, [null, null], null, null);
            const res = await fetchCommissionList(params, 1, pagination.pageSize);
            setCommissionData(res.list);
            setTotal(res.total);
            setCumulativeData({
                cumulativeCommission: res.total_commission,
                spot: res.spot_commission,
                futures: res.contract_commission,
                alpha: res.alpha_commission,
            });
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.commission.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            // Validate customerUID and orderID before querying
            if (customerUID.trim() !== '') {
                const validation = validateNumericID(customerUID, 'UID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            if (orderID.trim() !== '') {
                const validation = validateNumericID(orderID, 'orderID');
                if (!validation.isValid) {
                    toast.error({
                        message: t(validation.message)
                    });
                    return;
                }
            }

            const params = buildCommissionParams(customerUID, orderID, rebateType, transactionTime, tradingPair, transactionMethod);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchCommissionList(params, 1, pagination.pageSize);
            setCommissionData(res.list);
            setTotal(res.total);
            setCumulativeData({
                cumulativeCommission: res.total_commission,
                spot: res.spot_commission,
                futures: res.contract_commission,
                alpha: res.alpha_commission,
            });
        } catch (error: any) {
            toast.error({
                message: error?.message || t('financialDetails.commission.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    function exportToCsv(filename: string, rows: any[]) {
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const csvContent =
            keys.join(',') + '\n' +
            rows.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleExport = async () => {
        try {
            console.log('Exporting data...');
            setLoading(true);
            const params = buildCommissionParams(customerUID, orderID, rebateType, transactionTime, tradingPair, transactionMethod);
            const result = await exportData(params, pageType);
            
            // Export the data list to CSV
            if (result && result.list && result.list.length > 0) {
                exportToCsv('commission_data.csv', result.list);
                toast.success({
                    message: t('financialDetails.commission.exportSuccess') || 'Data exported successfully!'
                });
            } else {
                toast.warning({
                    message: t('financialDetails.commission.noDataToExport') || 'No data to export.'
                });
            }
        } catch (error: any) {
            console.error('Export failed:', error);
            toast.error({
                message: error?.message || t('financialDetails.commission.exportError') || 'Export failed. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    }

    const handleExportAffiliate = async () => {
        console.log('Exporting data...');
    }

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };
    
    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const formatPercentage = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return (Number(num) * 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '%';
    };

    const handleCopyUID = async (uid: string, orderID: string) => {
        try {
            await navigator.clipboard.writeText(uid);
            setCopiedOrderID(orderID);
            setTimeout(() => setCopiedOrderID(null), 2000);
            toast.success({
                message: t('customerManagement.clientList.copySuccess') || 'UID copied!'
            });
        } catch (err: any) {
            toast.error({
                message: err?.message || t('customerManagement.clientList.copyError') || 'Failed to copy UID.'
            });
        }
    };


    const depositMap: {value: number, label: string}[] = [
        { value: 0, label: t('financialDetails.commission.depositStatusOptions.unsettled') },
        { value: 1, label: t('financialDetails.commission.depositStatusOptions.pending') },
        { value: 2, label: t('financialDetails.commission.depositStatusOptions.completed') },
        { value: 3, label: t('financialDetails.commission.depositStatusOptions.cancelled') },
        { value: 4, label: t('financialDetails.commission.depositStatusOptions.frozen') }
    ]

    const queryFields: QueryField[] = [
        {
            type: 'input',
            name: 'customerUID',
            label: t('financialDetails.commission.customerUID'),
            value: customerUID,
            onChange: setCustomerUID,
            placeholder: t('financialDetails.commission.customerPlaceholder'),
        },
        {
            type: 'input',
            name: 'orderID',
            label: t('financialDetails.commission.orderID'),
            value: orderID,
            onChange: setOrderID,
            placeholder: t('financialDetails.commission.orderIDPlaceholder'),
        },
        // {
        //     type: 'select',
        //     name: 'rebateType',
        //     label: t('financialDetails.commission.rebateType'),
        //     value: rebateType,
        //     onChange: setRebateType,
        //     options: [
        //         { label: t('financialDetails.commission.all'), value: null },
        //         { label: t('financialDetails.commission.rebateTypeOptions.spot'), value: 1 },
        //         { label: t('financialDetails.commission.rebateTypeOptions.futures'), value: 2 },
        //         // { label: t('financialDetails.commission.rebateTypeOptions.alpha'), value: 3 },
        //     ]
        // },
        {
            type: 'dateRange',
            name: 'transactionTime',
            label: t('financialDetails.commission.transactionTime'),
            value: transactionTime,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTransactionTime(dates ?? [null, null]);
            },
            placeholder: [t('financialDetails.commission.startTime'), t('financialDetails.commission.endTime')],
        },
        {
            type: 'select',
            name: 'tradingPair',
            label: t('financialDetails.commission.tradingPair'),
            value: tradingPair,
            onChange: setTradingPair,
            options: [
                { label: t('financialDetails.commission.all'), value: null },
                ...contractCodes.map(code => ({ label: code, value: code }))
            ]
        },
        {
            type: 'select',
            name: 'transactionMethod',
            label: t('financialDetails.commission.transactionMethod'),
            value: transactionMethod,
            onChange: (value) => setCommissionMethod(value),
            options: [
                { label: t('financialDetails.commission.all'), value: null },
                { label: t('financialDetails.commission.buy'), value: 'B' },
                { label: t('financialDetails.commission.sell'), value: 'S' },
            ]
        }
    ];

    const commissionColumns: ColumnsType<Commission> = [
        {
            title: t('financialDetails.commission.table.customerUID'),
            dataIndex: 'customerUID',
            key: 'customerUID',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text: string, record: Commission) => (
                <div className="flex items-center gap-1">
                    <span>{text}</span>
                    <button
                        className="border-none bg-transparent p-0 m-0"
                        onClick={() => handleCopyUID(text, record.orderID)}
                    >
                        <img
                            src={copiedOrderID === record.orderID ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                            alt={copiedOrderID === record.orderID ? 'copied' : 'copy'}
                            className="w-4 h-4"
                        />
                    </button>
                </div>
            ),
        },
        {
            title: t('financialDetails.commission.table.remark'),
            dataIndex: 'remark',
            key: 'remark',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text: string) => text || '--'
        },
        {
            title: t('financialDetails.commission.table.orderID'),
            dataIndex: 'orderID',
            key: 'orderID',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{text || '--'}</span>
        },
        // {
        //     title: t('financialDetails.commission.table.customerGroup'),
        //     dataIndex: 'customerGroup',
        //     key: 'customerGroup',
        //     align: 'start',
        //     width: 130,
        //     ellipsis: true,
        //     render: (text) => text || '--'
        // },
        // {
        //     title: t('financialDetails.commission.table.associatedAgent'),
        //     dataIndex: 'associatedAgent',
        //     key: 'associatedAgent',
        //     align: 'start',
        //     width: 130,
        //     ellipsis: true,
        //     render: (text) => text && text !== '0' ? text : '--'
        // },
        {
            title: t('financialDetails.commission.table.superiorAgentUID'),
            dataIndex: 'superiorAgentUID',
            key: 'superiorAgentUID',
            align: 'start',
            width: 140,
            ellipsis: true,
            render: (text) => text && text !== '0' ? text : '--'
        },
        {
            title: t('financialDetails.commission.table.tradingPair'),
            dataIndex: 'tradingPair',
            key: 'tradingPair',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('financialDetails.commission.table.rebateRate'),
            dataIndex: 'rebateRate',
            key: 'rebateRate',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => formatPercentage(text)
        },
        {
            title: t('financialDetails.commission.table.rebateAmount'),
            dataIndex: 'rebateAmount',
            key: 'rebateAmount',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => formatNumber(text)
        },
        {
            title: t('financialDetails.commission.table.rebateTime'),
            dataIndex: 'rebateTime',
            key: 'rebateTime',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        {
            title: t('financialDetails.commission.table.transactionMethod'),
            dataIndex: 'transactionMethod',
            key: 'transactionMethod',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => text === 'B' ? t('financialDetails.commission.buy') : t('financialDetails.commission.sell')
        },
        {
            title: t('financialDetails.commission.table.depositStatus'),
            dataIndex: 'depositStatus',
            key: 'depositStatus',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (value: number) => depositMap[value]?.label || '--'
        },
    ];

    const mobileCommissionColumns = commissionColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                try {
                    const params = buildCommissionParams(customerUID, orderID, rebateType, transactionTime, tradingPair, transactionMethod);
                    const res = await fetchCommissionList(params, pagination.current, pagination.pageSize);
                    setCommissionData(res.list);
                    setTotal(res.total);
                    setCumulativeData({
                        cumulativeCommission: res.total_commission,
                        spot: res.spot_commission,
                        futures: res.contract_commission,
                        alpha: res.alpha_commission,
                    });
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    // Initial data fetch for contract codes
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const contractRes = await fetchContractCode();
                setContractCodes(contractRes);
            } catch (groupError: any) {
                toast.error({
                    message: groupError?.message || t('customerManagement.clientList.groupFetchError') || 'An error occurred while fetching group list.'
                });
            }
        };
        fetchInitialData();
    }, []);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('financialDetails.commission.title')}
                subtitle={t('financialDetails.commission.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                onExport={handleExport}
                gridCols="grid-cols-2 md:grid-cols-4"
            />

            <div className="mt-6 w-full gap-6">
                <div className="flex flex-col md:flex-row gap-4 p-6 bg-card-background dark:bg-card-background rounded-lg items-start">
                    <div>
                        <div className="w-full text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal">
                            {t('financialDetails.commission.cumulativeCommission')}
                        </div>
                        <div className="mt-5 w-full text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                            {formatNumber(cumulativeData?.cumulativeCommission || 0)}
                        </div>
                    </div>
                    <div className="px-6 py-4 w-full max-w-[465px] flex flex-row bg-button dark:bg-button-dark rounded-lg">
                        <div className="flex flex-col gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.spot || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.commission.spot')}
                            </div>
                        </div>
                        <div className="flex flex-col  gap-[6px] flex-1">
                            <div className="w-full h-6 items-center flex text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
                                {formatNumber(cumulativeData?.futures || 0)}
                            </div>
                            <div className="w-full h-6 items-center flex text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal">
                                {t('financialDetails.commission.futures')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Active Tab for Spot, Futures, and Alpha */}

            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={commissionData}
                        columns={commissionColumns}
                        rowKey="orderID"
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 850 }}
                        className="custom-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {commissionData.map((row, idx) => (
                        <MobileTableCard
                            key={row.orderID}
                            record={row}
                            columns={mobileCommissionColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination(prev => ({ ...prev, current: page }))}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default Commission;